// src/pages/user/DieuKhoanDieuKien.tsx
import React, { useEffect } from 'react';
import styled, { ThemeProvider, DefaultTheme } from 'styled-components';
import { useTranslation, Trans } from 'react-i18next';

// --- THEME VÀ TYPES ---
const theme = {
  colors: {
    background: '#f8f9fa',
    surface: '#ffffff',
    primary: '#38a169',
    text: '#4a5568',
    heading: '#1a202c',
    subtleText: '#718096',
  },
  fonts: {
    body: "'Inter', sans-serif",
  },
  fontSizes: {
    base: '1rem',
    h1: '2.5rem',
    h2: '1.8rem',
    h3: '1.3rem',
  },
  spacing: {
    sm: '8px',
    md: '16px',
    lg: '24px',
    xl: '32px',
    xxl: '48px',
  },
  shadows: {
    main: '0 8px 30px rgba(0, 0, 0, 0.08)',
  },
  borderRadius: '12px',
  breakpoints: {
    tablet: '768px',
  },
};

type AppTheme = typeof theme;

declare module 'styled-components' {
  export interface DefaultTheme extends AppTheme {}
}

// --- STYLED COMPONENTS ---
const PageContainer = styled.div`
  background-color: ${({ theme }) => theme.colors.background};
  padding: ${({ theme }) => theme.spacing.xxl} 15px;
  font-family: ${({ theme }) => theme.fonts.body};
  min-height: 100vh;
`;

const ContentCard = styled.div`
  max-width: 850px;
  margin: 0 auto;
  background-color: ${({ theme }) => theme.colors.surface};
  border-radius: ${({ theme }) => theme.borderRadius};
  padding: ${({ theme }) => theme.spacing.xxl};
  box-shadow: ${({ theme }) => theme.shadows.main};
  color: ${({ theme }) => theme.colors.text};
  line-height: 1.8;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: ${({ theme }) => theme.spacing.xl};
  }
`;

const Heading = styled.h2`
  margin-top: ${({ theme }) => theme.spacing.xxl};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  color: ${({ theme }) => theme.colors.heading};
  font-size: ${({ theme }) => theme.fontSizes.h2};
  font-weight: 700;
  border-bottom: 2px solid ${({ theme }) => theme.colors.primary};
  padding-bottom: ${({ theme }) => theme.spacing.md};
  line-height: 1.4;

  &:first-of-type {
    margin-top: 0;
  }
`;

const SubHeading = styled.h3`
  margin-top: ${({ theme }) => theme.spacing.xl};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  color: ${({ theme }) => theme.colors.heading};
  font-size: ${({ theme }) => theme.fontSizes.h3};
  font-weight: 700;
  line-height: 1.5;
`;

const Paragraph = styled.p`
  margin-bottom: ${({ theme }) => theme.spacing.md};
  font-size: ${({ theme }) => theme.fontSizes.base};
  text-align: justify;
`;

const List = styled.ul`
  list-style: none;
  padding-left: 0;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const ListItem = styled.li`
  margin-bottom: ${({ theme }) => theme.spacing.md};
  display: flex;
  align-items: flex-start;
  font-size: ${({ theme }) => theme.fontSizes.base};

  &::before {
    content: '✓';
    color: ${({ theme }) => theme.colors.primary};
    font-weight: 700;
    margin-right: 12px;
    font-size: 1.1em;
    margin-top: 1px;
    text-align: center; 
  }
`;


// --- COMPONENT CHÍNH ---
const DieuKhoanDieuKien: React.FC = () => {
  const { t } = useTranslation();

  useEffect(() => {
    document.title = t('termsPage.helmetTitle');
  }, [t]);

  return (
    <ThemeProvider theme={theme}>
        <PageContainer>
            <ContentCard>
                <Heading id="dieu-khoan-dieu-kien">{t('termsPage.title')}</Heading>

                <SubHeading>{t('termsPage.introduction.heading')}</SubHeading>
                <Paragraph>{t('termsPage.introduction.p1')}</Paragraph>
                <Paragraph>{t('termsPage.introduction.p2')}</Paragraph>

                <SubHeading>{t('termsPage.websiteUsage.heading')}</SubHeading>
                <Paragraph>{t('termsPage.websiteUsage.p1')}</Paragraph>
                <Paragraph>
                    <Trans
                      i18nKey="termsPage.websiteUsage.p2"
                      components={{
                        strong: <strong />,
                      }}
                    />
                </Paragraph>

                <SubHeading>{t('termsPage.trademarkCopyright.heading')}</SubHeading>
                <Paragraph>{t('termsPage.trademarkCopyright.p1')}</Paragraph>

                <SubHeading>{t('termsPage.legalRights.heading')}</SubHeading>
                <Paragraph>{t('termsPage.legalRights.p1')}</Paragraph>

                <SubHeading>{t('termsPage.privacyPolicy.heading')}</SubHeading>
                <Paragraph>{t('termsPage.privacyPolicy.p1')}</Paragraph>
                <Paragraph>{t('termsPage.privacyPolicy.p2')}</Paragraph>
                <Paragraph>{t('termsPage.privacyPolicy.p3')}</Paragraph>

                <SubHeading>{t('termsPage.paymentPolicy.heading')}</SubHeading>
                <Paragraph>{t('termsPage.paymentPolicy.p1')}</Paragraph>
                <List>
                    <ListItem>{t('termsPage.paymentPolicy.li1')}</ListItem>
                    <ListItem>{t('termsPage.paymentPolicy.li2')}</ListItem>
                </List>

                <SubHeading>{t('termsPage.disputeResolution.heading')}</SubHeading>
                <Paragraph>{t('termsPage.disputeResolution.p1')}</Paragraph>
            </ContentCard>
        </PageContainer>
    </ThemeProvider>
  );
};

export default DieuKhoanDieuKien;